// Six-level governance readiness as the servers answer it (A8 read contract,
// challenge record 2026-10-05-governance-a5-a6-a8-adoption-contracts, F-M5;
// agion/Agion#2853). Three reads:
//
// - governance GET /v1/governance/adoption-policy: six_level_active (the
//   environment and the organisation part together) and, for an organisation
//   owner or admin while active, sources {complete, incomplete}. The same
//   answer serves the organisation switch (PLAN-20260925 slice 2), which an
//   owner or admin changes with PUT {enabled, reason}. Where the environment
//   part is off the route is not mounted, and its 404 reads as inactive
//   (agion/Agion#3055, challenge record
//   2026-10-12-adoption-policy-route-flag-gate C3).
// - orchestration GET /v1/missions/governance-summary: governance_active and,
//   for an owner or admin, adopted and legacy mission counts.
// - orchestration GET /v1/missions/{id}/classification-readiness: the
//   mission's state (legacy or adopted) and the blockers to classifying it;
//   a viewer gets the codes only, an editor also the subjects they name. An
//   archived mission answers the state archived with no blockers and no
//   levels: it can never be classified (agion/Agion#3182). A legacy mission
//   whose classification someone chose to defer at publish also carries
//   classification_deferral: when, and for an editor who (agion/Agion#3203).
//
// The API client camelCases the answers; this module only checks their shape
// and types them. It decides nothing: a field that is missing or malformed
// stays unknown, and an answer that does not say governance is active reads
// as inactive.
import { api, ApiError } from '@/api'

/** The stable blocker codes of classification readiness, in the server's vocabulary. */
export const MISSION_ADOPTION_BLOCKERS = [
  'governance_unavailable',
  'governance_inactive',
  'source_unclassified',
  'source_location_missing',
  'source_unavailable',
  'host_unqualified',
  'no_candidate_host',
  'execution_active',
  'trigger_without_initiator',
  'channel_bound',
  'level_below_floor',
  'caller_not_eligible',
  'secret_requires_owner_admin',
] as const
export type MissionAdoptionBlockerCode = (typeof MISSION_ADOPTION_BLOCKERS)[number]

/** The object a blocker names; served only to a caller who may edit the mission. */
export interface ReadinessSubject {
  /** mission, connection, capsule, host, channel, execution or trigger. */
  type: string
  /** For links only; never shown. */
  id: string
  name: string
}

export interface ReadinessBlocker {
  /** One of MISSION_ADOPTION_BLOCKERS, or a code from a later server. */
  code: string
  subject?: ReadinessSubject
  /** A served value (a level, a fact list, a reason code); a viewer receives only a host's reason. */
  detail?: string
}

/**
 * A recorded "Classify later" choice as the readiness serves it: when, and for
 * an editor who chose it. A viewer is served the time only.
 */
export interface ClassificationDeferral {
  deferredAt: string
  /** Served to an editor only. The id is for links only; never shown. */
  deferredBy?: { id: string; name: string }
}

export type MissionClassificationReadiness =
  | { governanceActive: false }
  | {
      governanceActive: true
      /** archived: the mission can never be classified and is asked for nothing. */
      state: 'legacy' | 'adopted' | 'archived'
      /** The stored level, served to an editor; null when not served. */
      current: string | null
      /**
       * The strictest level of the mission's bound sources (Public with none),
       * served to an editor; absent when a source's level is unknown or the
       * server serves none.
       */
      floor?: string
      /**
       * The levels this caller may set now, in the server's order (at or above
       * the floor and the current level, Secret only for an owner or admin);
       * served to an editor, empty for a viewer.
       */
      offeredLevels: string[]
      blockers: ReadinessBlocker[]
      /** The recorded deferral; absent when there is none or the server serves no such field. */
      classificationDeferral?: ClassificationDeferral
    }

/** Adoption-policy answers this module reads: activation and, for an owner or admin, source counts. */
export interface AdoptionPolicyActivation {
  sixLevelActive: boolean
  sources?: { complete: number; incomplete: number }
}

export interface MissionGovernanceSummary {
  governanceActive: boolean
  /** Served to an organisation owner or admin only, while governance is active. */
  counts?: { adopted: number; legacy: number }
}

type Raw = Record<string, unknown>

const record = (value: unknown): Raw | undefined =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Raw) : undefined

const count = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : undefined

const text = (value: unknown): string | undefined => (typeof value === 'string' && value !== '' ? value : undefined)

function subjectOf(value: unknown): ReadinessSubject | undefined {
  const raw = record(value)
  const type = text(raw?.type)
  const id = text(raw?.id)
  if (!raw || !type || !id) return undefined
  return { type, id, name: typeof raw.name === 'string' ? raw.name : '' }
}

/** Served blockers (camelCased) as typed blockers; unreadable entries are dropped. */
export function blockersOf(value: unknown): ReadinessBlocker[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((entry) => {
    const raw = record(entry)
    const code = text(raw?.code)
    if (!raw || !code) return []
    const subject = subjectOf(raw.subject)
    const detail = text(raw.detail)
    return [{ code, ...(subject ? { subject } : {}), ...(detail ? { detail } : {}) }]
  })
}

/** A served deferral (camelCased), or undefined when none is served or it is malformed. */
export function classificationDeferralOf(value: unknown): ClassificationDeferral | undefined {
  const raw = record(value)
  const deferredAt = text(raw?.deferredAt)
  if (!raw || !deferredAt) return undefined
  const by = record(raw.deferredBy)
  const id = text(by?.id)
  return { deferredAt, ...(by && id ? { deferredBy: { id, name: typeof by.name === 'string' ? by.name : '' } } : {}) }
}

/** A classification-readiness answer (camelCased); anything but an active, well-formed answer is inactive. */
export function classificationReadinessOf(value: unknown): MissionClassificationReadiness {
  const raw = record(value)
  if (!raw || raw.governanceActive !== true) return { governanceActive: false }
  if (raw.state !== 'legacy' && raw.state !== 'adopted' && raw.state !== 'archived') return { governanceActive: false }
  const deferral = classificationDeferralOf(raw.classificationDeferral)
  const floor = text(raw.floor)
  return {
    governanceActive: true,
    state: raw.state,
    current: text(raw.current) ?? null,
    ...(floor ? { floor } : {}),
    offeredLevels: Array.isArray(raw.offeredLevels) ? raw.offeredLevels.filter((level): level is string => typeof level === 'string' && level !== '') : [],
    blockers: blockersOf(raw.blockers),
    ...(deferral ? { classificationDeferral: deferral } : {}),
  }
}

/** An adoption-policy answer (camelCased); six_level_active must be served as true to be active. */
export function adoptionPolicyActivationOf(value: unknown): AdoptionPolicyActivation {
  const raw = record(value)
  const sixLevelActive = raw?.sixLevelActive === true
  const sources = record(raw?.sources)
  const complete = count(sources?.complete)
  const incomplete = count(sources?.incomplete)
  return {
    sixLevelActive,
    ...(sixLevelActive && complete !== undefined && incomplete !== undefined ? { sources: { complete, incomplete } } : {}),
  }
}

/** A governance-summary answer (camelCased); counts only when both are served while active. */
export function missionGovernanceSummaryOf(value: unknown): MissionGovernanceSummary {
  const raw = record(value)
  const governanceActive = raw?.governanceActive === true
  const adopted = count(raw?.adopted)
  const legacy = count(raw?.legacy)
  return {
    governanceActive,
    ...(governanceActive && adopted !== undefined && legacy !== undefined ? { counts: { adopted, legacy } } : {}),
  }
}

/** GET /v1/missions/{id}/classification-readiness */
export async function getMissionClassificationReadiness(missionId: string): Promise<MissionClassificationReadiness> {
  return classificationReadinessOf(await api.get<unknown>(`missions/${encodeURIComponent(missionId)}/classification-readiness`))
}

/**
 * The mission's stored access_classification from GET /v1/missions/{id}
 * (camelCased); null for a legacy mission or a read without the field. For a
 * caller whose readiness answer serves no level (a viewer).
 */
export async function getMissionAccessClassification(missionId: string): Promise<string | null> {
  const raw = record(await api.get<unknown>(`missions/${encodeURIComponent(missionId)}`))
  const mission = record(raw?.mission) ?? raw
  return text(mission?.accessClassification) ?? null
}

/**
 * Whether a failed adoption-policy read is the route's absence: governance
 * mounts no adoption-policy route where this environment does not serve
 * six-level governance, so its 404 is the answer "not active", not a failure.
 */
function adoptionPolicyRouteAbsent(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404
}

/** GET /v1/governance/adoption-policy; the route's absence (404) is inactive, any other failure is thrown. */
export async function getAdoptionPolicyActivation(): Promise<AdoptionPolicyActivation> {
  try {
    return adoptionPolicyActivationOf(await api.get<unknown>('governance/adoption-policy'))
  } catch (error) {
    if (adoptionPolicyRouteAbsent(error)) return { sixLevelActive: false }
    throw error
  }
}

/** GET /v1/missions/governance-summary */
export async function getMissionGovernanceSummary(): Promise<MissionGovernanceSummary> {
  return missionGovernanceSummaryOf(await api.get<unknown>('missions/governance-summary'))
}

// ---------------------------------------------------------------------------
// The organisation switch (PLAN-20260925 slice 2)

/**
 * The environment reasons the adoption policy serves (governance-service
 * models.SixLevelGovernanceFlagOff, SixLevelGovernanceUIVariantEnvironmentLocked):
 * while one is served, this environment does not serve six-level governance.
 */
export const SIX_LEVEL_ENVIRONMENT_REASONS = ['six_level_governance_flag_off', 'ui_variant_environment_locked'] as const

/** The stable refusals of PUT /v1/governance/adoption-policy. */
export const ADOPTION_POLICY_REFUSALS = [
  'adoption_already_started',
  'governance_classified_sources',
  'governance_officer_required',
  'adoption_policy_reason_required',
  'adoption_policy_admin_required',
] as const
export type AdoptionPolicyRefusalCode = (typeof ADOPTION_POLICY_REFUSALS)[number]

/** A served refusal; governance_classified_sources also carries the two counts. */
export interface AdoptionPolicyRefusal {
  code: AdoptionPolicyRefusalCode
  classifiedConnections?: number
  classifiedCapsules?: number
}

/** The switch as the server serves it to a caller who may change it. */
export interface AdoptionPolicySwitch {
  enabled: boolean
  sixLevelActive: boolean
  /** Whether the server would accept switching off now. */
  canDisable: boolean
  /** Why switching off is refused, in the server's order. */
  disableReasons: string[]
  /** Why six-level governance is inactive for the organisation, in the server's order. */
  inactiveReasons: string[]
}

const strings = (value: unknown): string[] | undefined =>
  Array.isArray(value) && value.every((entry) => typeof entry === 'string') ? (value as string[]) : undefined

/**
 * An adoption-policy answer (camelCased) as the switch, or undefined when the
 * switch is not offered: the server did not say this caller can write, served
 * no six-level fields (an earlier release, or a caller it gives no reasons),
 * or names an environment reason. Every value shown is the server's.
 */
export function adoptionPolicySwitchOf(value: unknown): AdoptionPolicySwitch | undefined {
  const raw = record(value)
  if (!raw || raw.canWrite !== true) return undefined
  if (typeof raw.enabled !== 'boolean' || typeof raw.sixLevelActive !== 'boolean' || typeof raw.canDisable !== 'boolean') return undefined
  const inactiveReasons = strings(raw.inactiveReasons)
  const disableReasons = strings(raw.disableReasons)
  if (!inactiveReasons || !disableReasons) return undefined
  if (inactiveReasons.some((reason) => (SIX_LEVEL_ENVIRONMENT_REASONS as readonly string[]).includes(reason))) return undefined
  return { enabled: raw.enabled, sixLevelActive: raw.sixLevelActive, canDisable: raw.canDisable, disableReasons, inactiveReasons }
}

/** The served refusal of a failed switch write, when it is one of the known ones. */
export function adoptionPolicyRefusalOf(error: unknown): AdoptionPolicyRefusal | undefined {
  if (!(error instanceof ApiError)) return undefined
  const data = record(error.data)
  const code = ADOPTION_POLICY_REFUSALS.find((known) => known === data?.reason)
  if (!code) return undefined
  if (code !== 'governance_classified_sources') return { code }
  const classifiedConnections = count(data?.classifiedConnections)
  const classifiedCapsules = count(data?.classifiedCapsules)
  return {
    code,
    ...(classifiedConnections !== undefined ? { classifiedConnections } : {}),
    ...(classifiedCapsules !== undefined ? { classifiedCapsules } : {}),
  }
}

/** GET /v1/governance/adoption-policy, read as the switch; null when it is not offered, the route's absence (404) included. */
export async function getAdoptionPolicySwitch(): Promise<AdoptionPolicySwitch | null> {
  try {
    return adoptionPolicySwitchOf(await api.get<unknown>('governance/adoption-policy')) ?? null
  } catch (error) {
    if (adoptionPolicyRouteAbsent(error)) return null
    throw error
  }
}

/** PUT /v1/governance/adoption-policy {enabled, reason}; the reason is sent as typed and the server checks it. */
export async function setAdoptionPolicy(enabled: boolean, reason: string): Promise<AdoptionPolicySwitch | null> {
  return adoptionPolicySwitchOf(await api.put<unknown>('governance/adoption-policy', { enabled, reason })) ?? null
}
